/**
 * The frame's own boxes on the course pages (F5, C02 to C05): the teaching team, the course details, the course connections and the
 * course's Drive folder. Each view mounts its own Bento of these before its
 * owner's content; A3's and A4's views take the same boxes as `frameBoxes`
 * once they accept them.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { FactBox, ListBox, Read, Result, TaskBox } from "@/components/ui/box.schema";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { checkCommit, type CommitCheck } from "@/lib/api/commitSeams";
import { coursesApi, type Course, type TeachingAssignment } from "@/lib/api/courses";
import { teachingPublicationBox } from "@/components/registrar/course/teaching-publication-model";
import { registrarCourseCopy as publicationWords } from "@/components/registrar/course/registrar-course-copy";
import { inboxApi } from "@/lib/api/inbox";
import { driveContainersApi, driveContainersQueryKey, driveFolderUrl, type DriveContainer, type CourseDriveSetup } from "@/lib/api/driveContainers";
import { integrationReadinessQueryKey, integrationsApi, type CourseIntegrationReadiness } from "@/lib/api/integrations";

import { FRAME_COPY, meetingLine, modalityLine, termDates } from "./course-frame-copy";
import { ConnectionsBody } from "./CourseConnectionsBody";
import { isReady } from "./course-readiness";

const IMAM = { system: "imam" } as const;

/** A ready read at the time its query last answered. */
export function ready<T>(value: T, at: number): Read<T> {
  return { status: "ready", value, readAt: new Date(at || Date.now()).toISOString() };
}

/** A read the server refused for this person: it withdraws anything read before, never a stale keep (R1). */
export function refusedRead(error: unknown): boolean {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}

/** The instant a provider's record was read; the kit says it in the course's zone (C4, CASE-039). */
function readInstant(at: number): string | null {
  return at ? new Date(at).toISOString() : null;
}

/* ------------------------------------------------------------------ */
/* C02: the teaching team                                              */
/* ------------------------------------------------------------------ */

function useTeamRead(courseId: string): Read<TeachingAssignment[]> {
  const copy = FRAME_COPY.team;
  const team = useQuery({
    queryKey: ["course-teaching-team", courseId],
    queryFn: () => coursesApi.teachingTeam(courseId),
  });
  return refusedRead(team.error)
    ? { status: "forbidden" }
    : team.isPending
      ? { status: "loading" }
      : team.isError
        ? { status: "failed", sentence: copy.failed, retry: () => void team.refetch() }
        : team.data.length === 0
          ? { status: "empty", sentence: copy.empty }
          : ready(team.data, team.dataUpdatedAt);
}

const primaryFirst = (people: TeachingAssignment[]) =>
  [...people].sort((a, b) => Number(b.assignment_kind === "primary") - Number(a.assignment_kind === "primary"));

/** The primary instructor in the band's own form ("Ustadha Sakina Zaidi") when the band names them. */
const nameOf = (person: TeachingAssignment, bandName: string | null) =>
  person.assignment_kind === "primary" && bandName?.endsWith(person.display_name) ? bandName : person.display_name;

/**
 * Who the course inbox would let this seat write to: a channel is connected and
 * the inbox names the person available. A learner can't read a teacher's
 * standing, so the inbox, not the team read, decides Message (as the roster does).
 */
function useInboxRecipients(courseId: string): { settled: boolean; reachable: Set<string> } {
  const { me } = useAuth();
  const inbox = useQuery({
    queryKey: ["inbox", me?.tenant_id, me?.user_id, "context", courseId],
    queryFn: () => inboxApi.context(courseId),
    enabled: Boolean(me),
    retry: false,
  });
  const connected = Object.values(inbox.data?.channels ?? {}).some((channel) => channel.status === "active");
  return {
    // A failed inbox read settles too: the team draws, with no Message.
    settled: !me || !inbox.isPending,
    reachable: new Set(
      connected
        ? (inbox.data?.recipients ?? []).filter((person) => person.availability === "available").map((person) => person.user_id)
        : [],
    ),
  };
}

/**
 * People and Manage course: who teaches the course, said once as a sentence.
 * An assigned administrator changes it through the registrar's team visit
 * (`course-details-visit.ts`); everyone else reads it.
 */
export function useTeamBox(courseId: string, canChange = false): FactBox<TeachingAssignment[]> {
  const copy = FRAME_COPY.team;
  return {
    act: canChange ? { verb: copy.change, does: "visit", visit: `course:${courseId}/team` } : undefined,
    kind: "fact",
    id: `course-team-${courseId}`,
    job: copy.job,
    label: copy.label,
    owner: IMAM,
    fold: "choice",
    read: useTeamRead(courseId),
    prose: (people) => {
      const [first, ...others] = primaryFirst(people);
      return first.assignment_kind === "primary"
        ? copy.teaches(first.display_name, others.map((person) => person.display_name))
        : copy.teaches(null, people.map((person) => person.display_name));
    },
    detail: () => "",
  };
}

/**
 * A learner's People: the teachers, each with Message as its act, a visit to
 * the course conversation with them (falling back to the course's inbox until
 * A7 registers threads). A teacher with no active account has no Message.
 */
export function useLearnerTeamBox(courseId: string, bandName: string | null): ListBox<TeachingAssignment[], TeachingAssignment> {
  const copy = FRAME_COPY.team;
  const inbox = useInboxRecipients(courseId);
  const team = useTeamRead(courseId);
  return {
    kind: "list",
    id: `course-team-${courseId}`,
    job: copy.job,
    label: copy.label,
    owner: IMAM,
    fold: "never",
    // The rows draw once, with their acts: Message never arrives after its name.
    read: team.status === "ready" && !inbox.settled ? { status: "loading" } : team,
    detail: (people) => copy.count(people.length),
    state: (people) => ({ class: "quiet", word: copy.count(people.length) }),
    rows: primaryFirst,
    row: {
      key: (person) => person.id,
      title: (person) => nameOf(person, bandName),
      facts: (person) => (person.assignment_kind === "primary" ? copy.primary : copy.co),
      // thread:<course>/<recipient>: the recipient is the login the inbox addresses (A7), never a profile id.
      act: (person) =>
        person.faculty_user_id && inbox.reachable.has(person.faculty_user_id)
          ? { does: "visit", verb: copy.message, visit: `thread:${courseId}/${person.faculty_user_id}` }
          : undefined,
    },
  };
}

/* ------------------------------------------------------------------ */
/* C03: the course details                                             */
/* ------------------------------------------------------------------ */

interface Details {
  dates: string | null;
  meets: string | null;
  modality: string | null;
}

/**
 * What the band can't say (CASE-042): when the term runs and how the course
 * meets. The band owns term, CRN and zone. Edit details is a visit for anyone
 * the server lets edit the course, falling back until A6 registers it.
 */
export function useCourseRecord(courseId: string, enabled = true) {
  const { me } = useAuth();
  return useQuery({
    queryKey: ["course-information", me?.tenant_id, me?.user_id, courseId],
    queryFn: () => coursesApi.get(courseId),
    enabled,
    retry: false,
  });
}

export function useDetailsBox({ courseId, canEditDetails }: { courseId: string; canEditDetails: boolean }): FactBox<Details> {
  const copy = FRAME_COPY.details;
  const course = useCourseRecord(courseId);
  const recorded = course.data?.id === courseId ? course.data : null;
  const read: Read<Details> = refusedRead(course.error)
    ? { status: "forbidden" }
    : recorded
      ? ready(
          {
            dates: termDates(recorded.semester_start_date, recorded.semester_end_date),
            meets: meetingLine(recorded),
            modality: modalityLine(recorded.modality, recorded.location),
          },
          course.dataUpdatedAt,
        )
      : course.isError || course.isSuccess
        ? { status: "failed", sentence: copy.failed, retry: () => void course.refetch() }
        : { status: "loading" };
  const meets = (value: Details) => (value.meets ? FRAME_COPY.session.meets(value.meets).slice(0, -1) : null);
  return {
    kind: "fact",
    id: `course-details-${courseId}`,
    job: copy.job,
    label: copy.label,
    owner: IMAM,
    fold: "choice",
    read,
    // A field the record doesn't hold isn't drawn.
    prose: (value) => value.dates ?? meets(value) ?? copy.none,
    detail: (value) => [value.dates ? meets(value) : null, value.modality].filter(Boolean).join(" · "),
    act: canEditDetails ? { verb: copy.edit, does: "visit", visit: `course:${courseId}/details` } : undefined,
  };
}

/**
 * Manage course: the course's publication. A teacher reads the registrar's act
 * as a handoff (P3B-02, "Administration publishes"); an assigned administrator
 * counts as registrar and reviews publication and archive in the registrar's
 * own visit, hosted here.
 */
export function usePublicationBox(courseId: string, canReview: boolean): FactBox<Course> {
  const course = useCourseRecord(courseId);
  // A status the handoff has no words for is not guessed at: the read fails in place.
  const recorded = course.data?.id === courseId && Object.hasOwn(publicationWords.teachingPublication, course.data.status) ? course.data : null;
  const retry = () => void course.refetch();
  const read: Read<Course> = refusedRead(course.error)
    ? { status: "forbidden" }
    : recorded
      ? course.isError
        ? { status: "stale", last: recorded, readAt: new Date(course.dataUpdatedAt).toISOString(), sentence: FRAME_COPY.details.failed, refresh: retry }
        : ready(recorded, course.dataUpdatedAt)
      : course.isError || course.isSuccess
        ? { status: "failed", sentence: FRAME_COPY.details.failed, retry }
        : { status: "loading" };
  return {
    ...teachingPublicationBox(courseId, read),
    act: canReview ? { verb: FRAME_COPY.publication.review, does: "visit", visit: `course:${courseId}/publication` } : undefined,
  };
}

/**
 * Course connections is a Fact (CASE-037): IMAM has no repair of its own to
 * commit, so each provider's readiness names its owner and next step, and a
 * provider-side repair leaves for the provider's page in a new tab.
 */
export function useConnectionsBox(courseId: string): FactBox<CourseIntegrationReadiness> {
  const copy = FRAME_COPY.connections;
  const [checked, setChecked] = useState(false);
  const readiness = useQuery({
    queryKey: integrationReadinessQueryKey(courseId),
    queryFn: () => integrationsApi.getCourseReadiness(courseId),
    retry: false,
    enabled: checked,
  });
  const known = readiness.data?.course_id === courseId ? readiness.data : undefined;
  // A fresh refusal withdraws what an earlier read showed (R1); a transient fault keeps it, marked stale (R2).
  const read: Read<CourseIntegrationReadiness> = !checked
    ? { status: "unread", sentence: copy.unread }
    : refusedRead(readiness.error)
      ? { status: "forbidden" }
      : readiness.isPending
        ? { status: "loading" }
        : !known
          ? { status: "failed", sentence: copy.failed, detail: copy.failedDetail, retry: () => void readiness.refetch() }
          : readiness.isError
            ? { status: "stale", last: known, readAt: known.observed_at, sentence: copy.stale, refresh: () => void readiness.refetch() }
            : known.capabilities.length === 0
              ? { status: "empty", sentence: copy.none }
              : { status: "ready", value: known, readAt: known.observed_at };
  return {
    kind: "fact",
    id: `course-connections-${courseId}`,
    job: copy.job,
    label: copy.label,
    owner: IMAM,
    fold: "choice",
    read,
    // Unknown never draws ready (CASE-037): only a capability the read says is ready counts.
    prose: (value) => copy.answer(value.capabilities.filter(isReady).length, value.capabilities.length),
    detail: () => "",
    // Several people check a course's connections, so the kit says when this answer was read (CASE-039).
    readTime: true,
    body: (value) => <ConnectionsBody readiness={value} />,
    act: checked ? { verb: copy.show, does: "open" } : { verb: copy.check, does: "check", run: () => setChecked(true) },
  };
}

/* ------------------------------------------------------------------ */
/* C05: the course's Drive folder                                      */
/* ------------------------------------------------------------------ */

type DriveAnswer = { kind: "folder"; url: string } | { kind: "unknown" } | { kind: "none"; sentence: string };

/**
 * Drive owns the folder and IMAM holds its reference (C4). A Fact once a
 * folder exists; a Task while none does and the person may set one up. A
 * tenant without the capability answers 404, and the box isn't drawn, so the
 * tenant never learns it exists. A learner sees only a ready folder.
 */
export function useDriveBox({
  courseId,
  canEdit,
  termOpen,
  teaching,
  rank = null,
  registerRead,
}: {
  courseId: string;
  canEdit: boolean;
  termOpen: boolean;
  teaching: boolean;
  /** The course's zone, which the page passes to its Bento to say the read time. */
  timezone: string | null;
  /** The course page's ranking for Set it up, or unranked. */
  rank?: number | null;
  /** A complete cross-course read can supply this course without per-row fetches. */
  registerRead?: {
    data: { containers: DriveContainer[] } | undefined;
    dataUpdatedAt: number; error: Error | null; isError: boolean; isPending: boolean;
    refetch: () => Promise<unknown>;
  };
}): FactBox<DriveAnswer> | TaskBox<null> {
  const copy = FRAME_COPY.drive;
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const requestSlot = `imam:course-folder:${me?.tenant_id}:${me?.user_id}:${courseId}`;
  const [pendingKey, setPendingKey] = useState<string | null>(() => {
    try { return sessionStorage.getItem(requestSlot); } catch { return null; }
  });
  const [resumingKey] = useState(pendingKey);
  const retain = (key: string | null) => {
    setPendingKey(key);
    try { if (key) sessionStorage.setItem(requestSlot, key); else sessionStorage.removeItem(requestSlot); } catch { /* In-memory recovery remains available. */ }
  };
  const [result, setResult] = useState<Result>({ state: "none" });
  const localContainers = useQuery({
    queryKey: driveContainersQueryKey(courseId),
    queryFn: () => driveContainersApi.list(courseId),
    // A 404 is "this tenant has no folder surface", not a fault.
    retry: false,
    enabled: registerRead === undefined,
  });
  const containers = registerRead ?? localContainers;
  const id = `course-drive-${courseId}`;
  const owner = { system: "drive", readAt: readInstant(containers.dataUpdatedAt) } as const;
  const base = { id, job: copy.job, label: copy.label, owner, fold: "never" as const };
  const list = containers.data?.containers;
  const course = Array.isArray(list) ? list.find((item: DriveContainer) => item.session_id === null && item.state !== "failed") : undefined;
  const fact = (read: Read<DriveAnswer>, act?: FactBox<DriveAnswer>["act"]): FactBox<DriveAnswer> => ({
    ...base,
    kind: "fact",
    read,
    act,
    prose: (answer) => (answer.kind === "folder" ? copy.ready : answer.kind === "unknown" ? copy.unknownDetail : answer.sentence),
    detail: (answer) => (answer.kind === "folder" ? copy.readyDetail : answer.kind === "unknown" ? copy.unknownDetail : answer.sentence),
    sub: (answer) => (answer.kind === "folder" && teaching ? copy.readySub : ""),
  });

  // A fresh refusal withdraws a folder read before, and a setup's result with it; a tenant without folders never learns of one (R1).
  if (refusedRead(containers.error)) return fact({ status: "forbidden" });
  const folderUrl = course?.state === "ready" && course.drive_folder_id ? driveFolderUrl(course.drive_folder_id) : null;
  // A setup this visit made keeps its Task, so its result stays the receipt (W2).
  if (result.state === "none" && !pendingKey) {
    if (folderUrl && (!teaching || !canEdit || !termOpen || containers.isError)) {
      const answer: DriveAnswer = { kind: "folder", url: folderUrl };
      const leave = { verb: copy.open, does: "leave", providerUrl: folderUrl } as const;
      // A transient fault keeps the folder it read, marked stale (R2).
      return containers.isError
        ? fact({ status: "stale", last: answer, readAt: new Date(containers.dataUpdatedAt).toISOString(), sentence: copy.stale, refresh: () => void containers.refetch() }, leave)
        : fact(ready(answer, containers.dataUpdatedAt), leave);
    }
    // Below here there is no folder to open: a learner isn't told about one.
    if (!teaching) return fact({ status: "forbidden" });
    if (containers.isPending) return fact({ status: "loading" });
    if (!Array.isArray(list))
      return fact({ status: "failed", sentence: copy.failed, detail: copy.failedDetail, retry: () => void containers.refetch() });
    if (course?.state === "pending")
      return fact(
        { status: "unread", sentence: copy.pending, sub: copy.pendingSub },
        { verb: copy.checkAgain, does: "check", run: () => void containers.refetch() },
      );
    // An unknown outcome stops automation; reconciling it is an administrator's act (ADR 0017).
    if (course?.state === "unknown") return fact(ready({ kind: "unknown" }, containers.dataUpdatedAt));
    if (!folderUrl && (!canEdit || !termOpen))
      return fact(ready({ kind: "none", sentence: termOpen ? copy.denied : copy.closedTerm }, containers.dataUpdatedAt));
  }

  const reread = async () => {
    await queryClient.invalidateQueries({ queryKey: driveContainersQueryKey(courseId) });
    if (registerRead) await registerRead.refetch();
  };
  const confirmed = (): Result => ({ state: "confirmed", at: new Date().toISOString(), effects: [copy.effectFolder, copy.effectEditing], remains: [] });
  const accept = async (answer: CourseDriveSetup, requestId: string): Promise<Result> => {
    await reread();
    let next: Result;
    if (answer.state === "confirmed" && answer.container.state === "ready" && answer.actor_editing === "ready") {
      next = confirmed();
      retain(null);
    } else if (answer.container.state === "ready" && answer.actor_editing === "ready" && answer.recovery === "institution" && !answer.access_check_required) {
      next = { state: "confirmed", cap: copy.yourEditingReady, at: new Date().toISOString(),
        effects: [copy.folderExists, copy.effectOwnEditing], remains: [copy.verificationNeeded] };
      retain(null);
    } else if (answer.container.state === "failed") {
      next = { state: "refused", cap: copy.refused, sentence: copy.refusedFallback, draft: "kept" };
    } else if (answer.container.state === "ready" && !answer.access_check_required && answer.targets.some((target) => target.state === "retryable")) {
      next = {
        state: "partial", cap: copy.accessIncomplete, at: new Date().toISOString(), requestId,
        went: [{ sentence: copy.folderExists, ids: [courseId] }],
        failed: [{ sentence: copy.accessRetry, ids: [courseId] }],
        retry: { verb: copy.retryEditing, note: copy.retryNote, run: () => send(requestId, "retry") },
      };
    } else if (answer.container.state === "ready" && (answer.recovery === "institution" || (answer.recovery === undefined && answer.editing === "blocked"))) {
      next = {
        state: "partial", cap: answer.actor_editing === "ready" ? copy.yourEditingReady : copy.accessIncomplete,
        at: new Date().toISOString(), requestId,
        went: [{ sentence: copy.folderExists, ids: [courseId] }],
        failed: [{ sentence: answer.access_check_required ? copy.institutionRecovery : copy.verificationNeeded, ids: [courseId] }],
        retry: { verb: copy.check, note: copy.verificationNote, run: () => send(requestId, "check") },
      };
    } else next = uncertain(requestId, answer.container.state === "ready" ? copy.accessUnknown : answer.container.state === "unknown" ? copy.unknownDetail : copy.unknownResultSentence);
    setResult(next);
    return next;
  };
  const uncertain = (requestId: string, sentence: string = copy.unknownResultSentence): Result => ({
    state: "unknown", cap: copy.unknownResult, requestId, sentence,
    check: {
      verb: copy.check, note: copy.checkNote,
      run: async () => {
        try {
          const receipt: CommitCheck<CourseDriveSetup> = await checkCommit<CourseDriveSetup>(requestId);
          if (receipt.state === "not_committed") {
            const none: Result = { state: "none" }; retain(null); setResult(none); return none;
          }
          if (receipt.state === "pending") return uncertain(requestId, sentence);
          return await send(requestId, "check");
        } catch { return uncertain(requestId, sentence); }
      },
    },
  });
  const send = async (requestId: string, action: "setup" | "check" | "retry" = "setup"): Promise<Result> => {
    retain(requestId);
    try {
      const answer = await (action === "check" ? driveContainersApi.checkSetup : action === "retry" ? driveContainersApi.retrySetup : driveContainersApi.setup)(courseId, requestId);
      return await accept(answer, requestId);
    } catch (error) {
      const next: Result = error instanceof ApiError && error.status >= 400 && error.status < 500
        ? { state: "refused", cap: copy.refused, sentence: error.message || copy.refusedFallback, draft: "kept" }
        : uncertain(requestId);
      setResult(next);
      return next;
    }
  };
  const shownResult = result.state === "none" && pendingKey && pendingKey === resumingKey ? uncertain(pendingKey, copy.resumeRequest) : result;
  const prepared = Boolean(folderUrl && course?.editing_setup?.ready && shownResult.state === "none");
  return {
    ...base,
    fold: prepared ? "choice" : "never",
    kind: "task",
    read: ready(null, containers.dataUpdatedAt),
    state: () =>
      shownResult.state === "confirmed"
        ? { class: "done", word: copy.created }
        : shownResult.state === "unknown"
          ? { class: "reading", word: copy.unknownResult }
          : { class: "quiet", word: prepared ? copy.preparedState : folderUrl ? copy.linkedState : copy.noneState },
    detail: () => (prepared ? course?.editing_setup?.team_pending ? copy.teamWaiting : copy.preparedDetail : folderUrl ? copy.readyDetail : copy.noneDetail),
    act: prepared && folderUrl ? { verb: copy.open, does: "leave", providerUrl: folderUrl } : undefined,
    candidate: shownResult.state === "confirmed" || prepared ? undefined : { eligible: true, blocked: null, rank },
    exits: folderUrl ? prepared ? [{ label: copy.accessDetails, reopens: true }] : [{ label: copy.open, to: folderUrl, external: true }] : undefined,
    parts: [],
    commit: {
      verb: folderUrl ? copy.prepareEditing : copy.commit,
      cap: folderUrl ? copy.editingCap : copy.cap,
      effects: () => [
        { id: "folder", sentence: folderUrl ? copy.reuseFolder : copy.effectFolder },
        { id: "audience", sentence: copy.effectAudience },
        { id: "editing", sentence: copy.effectEditing },
      ],
      blockers: () => [],
      note: copy.editing,
      run: (requestId) => send(requestId),
    },
    result: shownResult,
    draft: false,
  };
}
